import React, { Component } from "react";

import { Divider, UIContainer, UINavigation, UINavigationItem } from "js/react/components/UiComponents";
import ProBadge from "js/react/components/ProBadge";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import { app } from "js/namespaces";
import { ds } from "js/core/models/dataService";
import { FeatureType } from "common/features";
import { $ } from "js/vendor";
import { Key } from "js/core/utilities/keys";
import { PresentationLibraryController } from "js/editor/PresentationLibrary/PresentationLibraryController";
import { trackActivity } from "js/core/utilities/utilities";
import ErrorHandler from "js/core/utilities/errorHandler";
import Api from "js/core/api";
import { UIController } from "js/editor/dialogs/UIController";
import { Gap20 } from "js/react/components/Gap";
import { Presentation } from "js/core/models/presentation";

import { ChooseThemePane } from "./ChooseThemePane";
import { ChooseStarterTemplatePane } from "./ChooseStarterTemplatePane";
import { ChooseTeamTemplatePane } from "./ChooseTeamTemplate";
import { ImportPresentationFromPPTPane } from "./ImportPresentationFromPPTPane";
import { BetaBadge } from "js/react/components/BetaBadge";
import { DesignerBotIcon } from "js/editor/AIGeneration/AiGenerationController";
import AICreatePresentationWizard from "js/editor/AIGeneration/AICreatePresentationWizard";
import { themeColors } from "js/react/sharedStyles";

export class CreateNewPresentationDialog extends Component {
    constructor() {
        super();

        this.state = {
            isOpen: true,
            selectedPaneId: "blank",
        };
    }

    componentDidMount() {
        $(window).on("keydown.add-slide-container", event => {
            if (event.which == Key.ESCAPE) {
                this.handleBackButton();
            }
        });
    }

    componentWillUnmount() {
        $(window).off("keydown.add-slide-container");
    }

    handleSelectPane = paneId => {
        this.setState({ selectedPaneId: paneId });
    }

    handleBackButton = () => {
        this.setState({ isOpen: false });
    }

    handleComplete = () => {
        this.setState({ isOpen: false });
    }

    handleCreatePresentation = async opts => {
        const {
            documentType,
            libraryFilter,
            isTeamTemplate,
            callback,
            closeDialog,
        } = this.props;

        try {
            let createdPresentation;
            switch (opts.type) {
                case "team-template":
                    createdPresentation = await PresentationLibraryController.createPresentationFromTemplate({
                        template: opts.template,
                        libraryFilter,
                    });
                    break;
                case "theme":
                    createdPresentation = await PresentationLibraryController.createPresentation({
                        documentType,
                        libraryFilter,
                        theme: opts.theme
                    });
                    break;
                case "starter-template":
                    createdPresentation = await Api.copyPresentation.post(
                        {
                            presentationId: opts.templateId,
                            workspaceId: UIController.getWorkspaceId()
                        }
                    ).then(({ presentationId }) => {
                        const props = {
                            presentationId: opts.templateId,
                            source: "template"
                        };
                        trackActivity("Presentation", "GetDeck", null, null, props, { audit: true });

                        return new Presentation({ id: presentationId });
                    });

                    break;
                case "ppt-import":
                    createdPresentation = await ds.presentations.getPresentation(opts.presentationId);
                    break;
                case "ai-template":
                    createdPresentation = await PresentationLibraryController.createPresentation({
                        name: opts.name,
                        documentType,
                        libraryFilter,
                        theme: opts.theme || ds.builtInThemes.sample(),
                        defaultSlides: opts.defaultSlides
                    });
                    trackActivity("Presentation", "Generate", null, null, {
                        presentationId: createdPresentation.id,
                        prompt: opts.description,
                        numSlides: opts.defaultSlides.length
                    });
                    break;
            }

            // Ensure the new presentation template
            //   triggers the create dialog when edited
            isTeamTemplate && createdPresentation.update({
                isTemplateConfirmed: false,
            });

            if (callback) {
                callback(createdPresentation);
            } else {
                PresentationLibraryController.editPresentation(createdPresentation, false);
            }

            closeDialog();
        } catch (err) {
            ErrorHandler.handleSlideLimitReached(err, { workspaceId: app.appState.workspaceId });
        }
    }

    render() {
        const { selectedPaneId, isOpen } = this.state;
        const workspaceId = app.appState.workspaceId;
        const designerBotDisabled = app.user.features.isFeatureEnabled(FeatureType.PROHIBIT_OPEN_AI_INTEGRATION, app.appState.workspaceId);
        const designerBotAccessible = app.user.features.isFeatureEnabled(FeatureType.DESIGNER_BOT, workspaceId);

        return (
            <UIContainer isOpen={isOpen} onClose={this.props.closeDialog}>
                <UINavigation
                    selected={selectedPaneId}
                    onSelect={this.handleSelectPane}
                    onBack={this.handleBackButton}
                >
                    <label>Start from...</label>
                    <Gap20 />
                    <UINavigationItem
                        key="blank"
                        id="blank"
                        title="Blank Presentation"
                        icon="palette"
                    />
                    <UINavigationItem
                        key="team-template"
                        id="team-template"
                        title="Team Templates"
                        icon="video_library"
                        proBadge={
                            <ProBadge
                                upgradeType={UpgradePlanDialogType.TEAM_NOOP}
                                show={!app.user.features.isFeatureEnabled(FeatureType.VIEW_LIBRARY_ITEMS, app.appState.workspaceId)}
                                analytics={{ cta: "TeamTemplates" }}
                                workspaceId={app.appState.workspaceId}
                            />
                        }
                    />
                    {
                        app.user.features.isFeatureEnabled(FeatureType.WORKSPACE_CAN_ACCESS_INSPIRATION_SLIDES, app.appState.workspaceId) &&
                        <UINavigationItem
                            key="template"
                            id="template"
                            title="Starter Templates"
                            icon="lightbulb"
                        />
                    }
                    <UINavigationItem
                        key="ppt-import"
                        id="ppt-import"
                        title="Import PPT"
                        icon="swap_vert"
                    />
                    {!designerBotDisabled && <Divider color="#555" />}
                    {!designerBotDisabled &&
                        <UINavigationItem
                            key="ai-template"
                            id="ai-template"
                            title="Designer Bot AI"
                            image={<DesignerBotIcon />}
                            styles={{ background: themeColors.aiColor, color: "white" }}
                            proBadge={
                                !designerBotAccessible &&
                                <ProBadge
                                    upgradeType={UpgradePlanDialogType.UPGRADE_PLAN}
                                    show={!designerBotAccessible}
                                    analytics={{ cta: "PresentationBot", ...ds.selection?.presentation?.getAnalytics() }}
                                    workspaceId={workspaceId}
                                />
                            }
                        />
                    }
                </UINavigation>

                {this.renderSelectedPane()}

            </UIContainer>
        );
    }

    renderSelectedPane = () => {
        let { requireSharedTheme } = this.props;
        const { selectedPaneId } = this.state;

        switch (selectedPaneId) {
            case "blank":
                return (<ChooseThemePane onCreate={theme => this.handleCreatePresentation({ type: "theme", theme })}
                    requireSharedTheme={requireSharedTheme} />);
            case "template":
                return <ChooseStarterTemplatePane onCreate={this.handleCreatePresentation} />;
            case "team-template":
                return <ChooseTeamTemplatePane onCreate={this.handleCreatePresentation} />;
            case "ai-template":
                return (<AICreatePresentationWizard onCreate={this.handleCreatePresentation}
                    requireSharedTheme={requireSharedTheme} />);
            case "ppt-import":
                return <ImportPresentationFromPPTPane onCreate={this.handleCreatePresentation} />;
        }
    }
}
